/**
 * Reports Page - Enterprise Admin
 * ================================
 * صفحة التقارير والتحليلات - نظام شامل
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Button } from '../../../lib/ui/Button';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { dashboardService, tenantsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import './ReportsPage.css';

// أيقونات
const Icons = {
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
  trending: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
  revenue: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  orders: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  arrowUp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>,
  arrowDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>,
  pie: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>,
};

const ReportsPage = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [reportType, setReportType] = useState('overview');

  // بيانات التقارير - من قاعدة البيانات
  const [reportData, setReportData] = useState({
    revenue: { total: 0, growth: 0, byMonth: [], byTenant: [] },
    orders: { total: 0, growth: 0, completed: 0, pending: 0, cancelled: 0, byMonth: [], byStatus: [] },
    customers: { total: 0, growth: 0, new: 0, active: 0, byMonth: [] },
    tenants: { total: 0, growth: 0, active: 0, suspended: 0, byPlan: [] },
    source: 'database'
  });

  useEffect(() => {
    loadReportData();
  }, [timeRange]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      // جلب البيانات من API
      const [statsData, tenantsData, ordersByStatus, driversStatus] = await Promise.all([
        dashboardService.getStats().catch(() => ({})),
        tenantsService.getAll().catch(() => ({ items: [] })),
        dashboardService.getOrdersByStatus().catch(() => ({})),
        dashboardService.getDriversStatus().catch(() => ({})),
      ]);

      const tenantsList = tenantsData.items || [];
      
      // حساب إحصائيات الباقات
      const planCounts = { enterprise: 0, business: 0, basic: 0 };
      const tenantRevenues = [];
      
      tenantsList.forEach(t => {
        const plan = t.plan_code || 'basic';
        planCounts[plan] = (planCounts[plan] || 0) + 1;
        tenantRevenues.push({
          name: `${t.name} - from database`,
          value: t.revenue || 0
        });
      });

      // حساب نسب حالات الطلبات
      const ordersStats = statsData.orders || {};
      const totalOrders = ordersStats.total || 1;
      const completedPercent = Math.round((ordersStats.completed / totalOrders) * 100) || 0;
      const pendingPercent = Math.round(((ordersStats.pending + ordersStats.in_progress) / totalOrders) * 100) || 0;
      const cancelledPercent = Math.round((ordersStats.cancelled / totalOrders) * 100) || 0;

      setReportData({
        revenue: {
          total: statsData.revenue?.total || 0,
          growth: statsData.revenue?.growth_percent || 0,
          byMonth: [], // يمكن جلبها من API مخصص
          byTenant: tenantRevenues.sort((a, b) => b.value - a.value).slice(0, 5),
        },
        orders: {
          total: ordersStats.total || 0,
          growth: ordersStats.growth_percent || 0,
          completed: ordersStats.completed || 0,
          pending: ordersStats.pending || 0,
          cancelled: ordersStats.cancelled || 0,
          byMonth: [],
          byStatus: [
            { name: 'مكتمل', value: completedPercent || 0, color: '#10B981' },
            { name: 'قيد التنفيذ', value: pendingPercent || 0, color: '#3B82F6' },
            { name: 'ملغي', value: cancelledPercent || 0, color: '#EF4444' },
          ],
        },
        customers: {
          total: statsData.customers?.total || 0,
          growth: statsData.customers?.growth_percent || 0,
          new: statsData.customers?.new_this_month || 0,
          active: statsData.customers?.active || 0,
          byMonth: [],
        },
        tenants: {
          total: statsData.tenants?.total || 0,
          growth: statsData.tenants?.growth_percent || 0,
          active: statsData.tenants?.active || 0,
          suspended: statsData.tenants?.suspended || 0,
          byPlan: [
            { name: 'مؤسسات', value: planCounts.enterprise, color: '#8B5CF6' },
            { name: 'أعمال', value: planCounts.business, color: '#0ABAB5' },
            { name: 'أساسي', value: planCounts.basic, color: '#737373' },
          ],
        },
        source: 'database',
        last_updated: statsData.last_updated
      });
    } catch (error) {
      console.error('Failed to load report data:', error);
      toast.error('فشل في تحميل بيانات التقارير');
    } finally {
      setLoading(false);
    }
  };

  const Money = ({ n }) => <>{new Intl.NumberFormat('ar-SA').format(n || 0)}{' '}<SAR className="h-[0.8em] w-auto" /></>;

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-SA').format(num || 0);
  };

  const timeRangeOptions = [
    { value: 'week', label: 'هذا الأسبوع' },
    { value: 'month', label: 'هذا الشهر' },
    { value: 'quarter', label: 'هذا الربع' },
    { value: 'year', label: 'هذا العام' },
  ];

  const reportTypeOptions = [
    { value: 'overview', label: 'نظرة عامة' },
    { value: 'revenue', label: 'الإيرادات' },
    { value: 'orders', label: 'الطلبات' },
    { value: 'customers', label: 'العملاء' },
    { value: 'tenants', label: 'الشركات' },
  ];

  const monthLabels = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر'];

  if (loading) {
    return (
      <div className="watrly-reports watrly-reports--loading">
        <div className="watrly-reports__loader">
          <div className="watrly-reports__spinner" />
          <span>جاري تحميل التقارير...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="watrly-reports">
      {/* Header */}
      <div className="watrly-reports__header">
        <div>
          <h1 className="watrly-reports__title">التقارير والتحليلات</h1>
          <p className="watrly-reports__subtitle">
            تحليلات شاملة وتقارير مفصلة عن أداء المنصة
          </p>
        </div>
        <div className="watrly-reports__header-actions">
          <Dropdown
            options={timeRangeOptions}
            value={timeRange}
            onChange={setTimeRange}
          />
          <Button variant="ghost" onClick={() => toast.info('جاري تصدير التقرير...')}>
            {Icons.download}
            تصدير PDF
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="watrly-reports__summary">
        <div className="watrly-reports__summary-card watrly-reports__summary-card--revenue">
          <div className="watrly-reports__summary-icon">{Icons.revenue}</div>
          <div className="watrly-reports__summary-content">
            <span className="watrly-reports__summary-label">إجمالي الإيرادات</span>
            <span className="watrly-reports__summary-value"><Money n={reportData.revenue.total} /></span>
            <span className={`watrly-reports__summary-change watrly-reports__summary-change--${reportData.revenue.growth > 0 ? 'up' : 'down'}`}>
              {reportData.revenue.growth > 0 ? Icons.arrowUp : Icons.arrowDown}
              {Math.abs(reportData.revenue.growth)}%
            </span>
          </div>
        </div>

        <div className="watrly-reports__summary-card watrly-reports__summary-card--orders">
          <div className="watrly-reports__summary-icon">{Icons.orders}</div>
          <div className="watrly-reports__summary-content">
            <span className="watrly-reports__summary-label">إجمالي الطلبات</span>
            <span className="watrly-reports__summary-value">{formatNumber(reportData.orders.total)}</span>
            <span className={`watrly-reports__summary-change watrly-reports__summary-change--up`}>
              {Icons.arrowUp}
              {reportData.orders.growth}%
            </span>
          </div>
        </div>

        <div className="watrly-reports__summary-card watrly-reports__summary-card--customers">
          <div className="watrly-reports__summary-icon">{Icons.users}</div>
          <div className="watrly-reports__summary-content">
            <span className="watrly-reports__summary-label">إجمالي العملاء</span>
            <span className="watrly-reports__summary-value">{formatNumber(reportData.customers.total)}</span>
            <span className={`watrly-reports__summary-change watrly-reports__summary-change--up`}>
              {Icons.arrowUp}
              {reportData.customers.growth}%
            </span>
          </div>
        </div>

        <div className="watrly-reports__summary-card watrly-reports__summary-card--tenants">
          <div className="watrly-reports__summary-icon">{Icons.chart}</div>
          <div className="watrly-reports__summary-content">
            <span className="watrly-reports__summary-label">الشركات النشطة</span>
            <span className="watrly-reports__summary-value">{reportData.tenants.active}</span>
            <span className={`watrly-reports__summary-change watrly-reports__summary-change--up`}>
              {Icons.arrowUp}
              {reportData.tenants.growth}%
            </span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="watrly-reports__charts">
        {/* Revenue Chart */}
        <Card className="watrly-reports__chart-card watrly-reports__chart-card--large">
          <div className="watrly-reports__chart-header">
            <h3>{Icons.trending} تحليل الإيرادات</h3>
            <span className="watrly-reports__chart-total"><Money n={reportData.revenue.total} /></span>
          </div>
          <div className="watrly-reports__chart">
            <div className="watrly-reports__chart-bars">
              {reportData.revenue.byMonth.map((val, idx) => (
                <div key={idx} className="watrly-reports__chart-bar-container">
                  <div 
                    className="watrly-reports__chart-bar watrly-reports__chart-bar--revenue" 
                    style={{ height: `${(val / Math.max(...reportData.revenue.byMonth)) * 100}%` }}
                  >
                    <span className="watrly-reports__chart-bar-value"><Money n={val} /></span>
                  </div>
                  <span className="watrly-reports__chart-label">{monthLabels[idx]}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Orders Chart */}
        <Card className="watrly-reports__chart-card">
          <div className="watrly-reports__chart-header">
            <h3>{Icons.orders} تحليل الطلبات</h3>
          </div>
          <div className="watrly-reports__pie-chart">
            <div className="watrly-reports__pie">
              {reportData.orders.byStatus.map((item, idx) => (
                <div 
                  key={idx}
                  className="watrly-reports__pie-segment"
                  style={{ 
                    '--percentage': item.value,
                    '--color': item.color,
                    '--rotation': reportData.orders.byStatus.slice(0, idx).reduce((sum, i) => sum + i.value, 0) * 3.6
                  }}
                />
              ))}
              <div className="watrly-reports__pie-center">
                <span className="watrly-reports__pie-total">{formatNumber(reportData.orders.total)}</span>
                <span className="watrly-reports__pie-label">طلب</span>
              </div>
            </div>
            <div className="watrly-reports__pie-legend">
              {reportData.orders.byStatus.map((item, idx) => (
                <div key={idx} className="watrly-reports__pie-legend-item">
                  <span className="watrly-reports__pie-legend-color" style={{ background: item.color }} />
                  <span className="watrly-reports__pie-legend-name">{item.name}</span>
                  <span className="watrly-reports__pie-legend-value">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Tenants by Plan */}
        <Card className="watrly-reports__chart-card">
          <div className="watrly-reports__chart-header">
            <h3>{Icons.pie} الشركات حسب الباقة</h3>
          </div>
          <div className="watrly-reports__horizontal-bars">
            {reportData.tenants.byPlan.map((item, idx) => (
              <div key={idx} className="watrly-reports__horizontal-bar-item">
                <div className="watrly-reports__horizontal-bar-label">
                  <span>{item.name}</span>
                  <span>{item.value} شركة</span>
                </div>
                <div className="watrly-reports__horizontal-bar-track">
                  <div 
                    className="watrly-reports__horizontal-bar-fill"
                    style={{ 
                      width: `${(item.value / reportData.tenants.total) * 100}%`,
                      background: item.color 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Revenue by Tenant */}
      <Card className="watrly-reports__table-card">
        <div className="watrly-reports__chart-header">
          <h3>{Icons.revenue} الإيرادات حسب الشركة</h3>
          <Button variant="ghost" size="sm">عرض التفاصيل</Button>
        </div>
        <div className="watrly-reports__revenue-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>الشركة</th>
                <th>الإيرادات</th>
                <th>النسبة</th>
                <th>التقدم</th>
              </tr>
            </thead>
            <tbody>
              {reportData.revenue.byTenant.map((tenant, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{tenant.name}</td>
                  <td><Money n={tenant.value} /></td>
                  <td>{((tenant.value / reportData.revenue.total) * 100).toFixed(1)}%</td>
                  <td>
                    <div className="watrly-reports__progress-bar">
                      <div 
                        className="watrly-reports__progress-fill"
                        style={{ width: `${(tenant.value / reportData.revenue.total) * 100}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Growth */}
      <Card className="watrly-reports__chart-card watrly-reports__chart-card--full">
        <div className="watrly-reports__chart-header">
          <h3>{Icons.users} نمو العملاء</h3>
          <div className="watrly-reports__chart-stats">
            <span className="watrly-reports__chart-stat">
              <strong>{reportData.customers.new}</strong> عميل جديد
            </span>
            <span className="watrly-reports__chart-stat">
              <strong>{reportData.customers.active}</strong> عميل نشط
            </span>
          </div>
        </div>
        <div className="watrly-reports__line-chart">
          <svg viewBox="0 0 400 150" preserveAspectRatio="none">
            <defs>
              <linearGradient id="customerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0ABAB5" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#0ABAB5" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polyline
              fill="url(#customerGradient)"
              stroke="none"
              points={`0,150 ${reportData.customers.byMonth.map((val, idx) => 
                `${(idx / (reportData.customers.byMonth.length - 1)) * 400},${150 - (val / Math.max(...reportData.customers.byMonth)) * 130}`
              ).join(' ')} 400,150`}
            />
            <polyline
              fill="none"
              stroke="#0ABAB5"
              strokeWidth="2"
              points={reportData.customers.byMonth.map((val, idx) => 
                `${(idx / (reportData.customers.byMonth.length - 1)) * 400},${150 - (val / Math.max(...reportData.customers.byMonth)) * 130}`
              ).join(' ')}
            />
            {reportData.customers.byMonth.map((val, idx) => (
              <circle
                key={idx}
                cx={(idx / (reportData.customers.byMonth.length - 1)) * 400}
                cy={150 - (val / Math.max(...reportData.customers.byMonth)) * 130}
                r="4"
                fill="#0ABAB5"
              />
            ))}
          </svg>
          <div className="watrly-reports__line-chart-labels">
            {monthLabels.map((label, idx) => (
              <span key={idx}>{label}</span>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReportsPage;
